import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { User, Building, Key, Mail, Calendar, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    toast.success('API key copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-gray-500">Failed to load profile</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Profile</h1>
        <p className="text-slate-600 mt-1">Manage your account and API keys</p>
      </div>

      {/* User Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-500">Full Name</label>
              <p className="text-lg font-semibold text-slate-900">{profile.user.full_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </label>
              <p className="text-lg font-semibold text-slate-900">{profile.user.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <Building className="h-3 w-3" />
                Organization
              </label>
              <p className="text-lg font-semibold text-slate-900">{profile.user.organization}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Member Since
              </label>
              <p className="text-lg font-semibold text-slate-900">
                {parseUtc(profile.user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Information */}
      <Card>
        <CardHeader>
          <CardTitle>Plan & Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-slate-500">Current Plan</label>
              <p className="text-lg font-semibold text-slate-900 capitalize">{profile.plan.type}</p>
            </div>
            <Badge className="bg-blue-100 text-blue-700 border-0">
              {profile.plan.type.toUpperCase()}
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <label className="text-sm font-medium text-slate-500">Clusters</label>
              <p className="text-lg font-semibold text-slate-900">
                {profile.usage.cluster_count} / {profile.usage.cluster_limit}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">vCPUs</label>
              <p className="text-lg font-semibold text-slate-900">
                {profile.usage.total_vcpus} / {profile.usage.vcpu_limit}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <label className="text-sm font-medium text-slate-500">Features</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {profile.plan.features.map((feature) => (
                <Badge key={feature} variant="outline" className="text-xs">
                  {feature.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Keys
          </CardTitle>
        </CardHeader>
        <CardContent>
          {profile.api_keys && profile.api_keys.length > 0 ? (
            <div className="space-y-3">
              {profile.api_keys.map((key) => (
                <div
                  key={key.id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Key className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-900">{key.name}</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded border border-slate-200 font-mono text-sm text-slate-700 break-all">
                        {key.key}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>Created: {parseUtc(key.created_at).toLocaleDateString()}</span>
                        {key.last_used && (
                          <span>Last used: {parseUtc(key.last_used).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(key.key, key.id)}
                      className="ml-2"
                    >
                      {copiedKey === key.id ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Key className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No API keys found</p>
              <p className="text-sm mt-1">API keys are generated when you add clusters in agent mode</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
